"use client";

import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { VALID_LANGUAGES } from "@/constants/i18n";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import useBoundStore from "@/store/bound-store";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { cleanRouteParamFromLangPrefix, matchRouteParam } from "@/utils/route";

interface ControlledProcessGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const routePatternsThatShouldCheckControlledProcess = [
  /^\/completed$/, // Matches completed processes list route
  /^\/enhance$/, // Matches enhance routes
  /^\/reports$/, // Matches reports routes
  /^\/settings\/.*$/, // Matches settings routes
  /^\/jobs$/, // Matches jobboard routes
  /^\/$/, // Matches root route
];

const controlledStandardAllowedRoutes = [
  /^\/reports.*$/, // Matches reports routes and subroutes
  /^\/enhance$/, // Matches enhance routes
  /^\/settings.*$/, // Matches settings routes and subroutes
];

export function ControlledProcessGuard({ children, fallback = null }: ControlledProcessGuardProps) {
  const environment = usePersistedBoundStore.use.environment();
  const personData = useBoundStore.use.person();
  const hasHydrated = usePersistedBoundStore.use.hasHydrated();
  const currentUserManager = useCurrentUserManager();
  const pathname = usePathname();
  const { navigateTo } = useLocalizedRouter();

  const cleanPath = cleanRouteParamFromLangPrefix(pathname, VALID_LANGUAGES);

  const shouldCheckControlledProcess =
    isEmpty(routePatternsThatShouldCheckControlledProcess) ||
    matchRouteParam(cleanPath, routePatternsThatShouldCheckControlledProcess);

  const [isControlled, setIsControlled] = useState(true);

  useEffect(() => {
    if (!shouldCheckControlledProcess || !currentUserManager || !hasHydrated) {
      return;
    }
    const controlled = get(environment, "environmentType.Name") === "CONTROLLED";
    const isProfileComplete = !!get(personData, "completeProfile", false);
    const isControlledStandard = controlled && isProfileComplete;

    const isAllowedRouteForControlledStandard = matchRouteParam(
      cleanPath,
      controlledStandardAllowedRoutes
    );

    setIsControlled(controlled);

    if (controlled && !isControlledStandard) {
      const environmentId = get(environment, "id");
      navigateTo(`/process/${environmentId}`);
      return;
    }

    if (isControlledStandard && !isAllowedRouteForControlledStandard) {
      const environmentId = get(environment, "id");
      navigateTo(`/process/${environmentId}`);
      return;
    }
  }, [
    currentUserManager,
    hasHydrated,
    environment,
    personData,
    shouldCheckControlledProcess,
    navigateTo,
    cleanPath,
  ]);

  if (!shouldCheckControlledProcess) {
    return <>{children}</>;
  }

  const isProfileComplete = !!get(personData, "completeProfile", false);
  const isControlledStandard = isControlled && isProfileComplete;
  const isAllowedRouteForControlledStandard = matchRouteParam(
    cleanPath,
    controlledStandardAllowedRoutes
  );

  if (isControlled && !isControlledStandard) {
    return <>{fallback}</>;
  }

  if (isControlledStandard && !isAllowedRouteForControlledStandard) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
